import React, { useState } from 'react';
import { synchroniser, RAISONS } from '../stockage/sync.js';
import { ecrireReglages } from '../stockage/reglages.js';
import Info from './Info.jsx';

/**
 * Section Synchronisation de Réglages - document 04, écran 3.
 *
 * L'indicateur est « discret mais toujours visible, jamais anxiogène »
 * (document 05). Une synchro qui échoue n'est pas une panne : le serveur est un
 * miroir (AD-1), l'application reste pleinement utilisable. Les messages le
 * disent explicitement plutôt que d'afficher une erreur rouge sur un état
 * normal.
 */
export default function Synchronisation({ reglages, onReglages, onDonneesChangees }) {
  const [brouillon, setBrouillon] = useState({
    syncUrl: reglages.syncUrl,
    deviceToken: reglages.deviceToken,
    deviceName: reglages.deviceName,
  });
  const [etat, setEtat] = useState({ type: 'inactif' });

  const enregistrer = async (partiel) => {
    const fusion = { ...brouillon, ...partiel };
    setBrouillon(fusion);
    onReglages(await ecrireReglages(partiel));
  };

  const lancer = async () => {
    setEtat({ type: 'encours' });
    try {
      const { resume, lastSyncAt } = await synchroniser();
      onReglages({ ...reglages, ...brouillon, lastSyncAt });
      await onDonneesChangees();
      setEtat({ type: 'ok', message: resume });
    } catch (e) {
      setEtat({ type: 'echec', raison: e.raison ?? RAISONS.SERVEUR, message: e.message });
    }
  };

  const champ = (cle, etiquette, type = 'text', aide, info = null) => (
    <label style={{ display: 'block' }}>
      <span className="etiquette">{etiquette}{info}</span>
      <input
        type={type}
        value={brouillon[cle]}
        onChange={(e) => setBrouillon((b) => ({ ...b, [cle]: e.target.value }))}
        onBlur={(e) => e.target.value !== reglages[cle] && enregistrer({ [cle]: e.target.value })}
        className="champ"
        style={{ marginTop: 'var(--espace-1)' }}
        autoComplete="off"
      />
      {aide && <span style={{ fontSize: '0.8rem', color: 'var(--couleur-encre-douce)' }}>{aide}</span>}
    </label>
  );

  return (
    <section>
      <h2 style={{ fontFamily: 'var(--police-titre)', fontSize: '1.1rem', margin: '0 0 var(--espace-3)' }}>
        Synchronisation
      </h2>

      <div style={{ display: 'grid', gap: 'var(--espace-3)', maxWidth: 520 }}>
        {champ('syncUrl', 'URL du serveur', 'url', 'Par exemple https://corridorfx-dev.acdspace.net/api')}
        {champ('deviceToken', "Jeton d'appareil", 'password', undefined,
          <Info titre="Jeton d’appareil">
            Le mot de passe partagé entre vos appareils et le serveur de
            synchronisation. Il n’identifie pas un utilisateur, il autorise un
            appareil. Il n’est jamais inclus dans une sauvegarde exportée :
            un fichier qui le promènerait ne serait plus une sauvegarde.
          </Info>)}
        {champ('deviceName', 'Nom de cet appareil', 'text', 'Pour s’y retrouver - « iPhone », « PC bureau »')}

        <div style={{ display: 'flex', gap: 'var(--espace-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={lancer}
            disabled={etat.type === 'encours' || !brouillon.syncUrl || !brouillon.deviceToken}
            style={{
              padding: 'var(--espace-2) var(--espace-4)',
              border: '1px solid var(--couleur-accent)', borderRadius: 'var(--rayon)',
              background: 'var(--couleur-accent)', color: 'var(--couleur-sur-accent)', fontWeight: 600,
              opacity: etat.type === 'encours' || !brouillon.syncUrl || !brouillon.deviceToken ? 0.5 : 1,
            }}
          >
            {etat.type === 'encours' ? 'Synchronisation…' : 'Synchroniser maintenant'}
          </button>

          <span className="chiffre" style={{ fontSize: '0.8rem', color: 'var(--couleur-encre-douce)' }}>
            {reglages.lastSyncAt
              ? `Dernière synchro ${new Date(reglages.lastSyncAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`
              : 'Jamais synchronisé'}
          </span>
        </div>

        <div aria-live="polite" style={{ minHeight: '1.25rem' }}>
          {etat.type === 'ok' && (
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--couleur-succes)' }}>
              Synchronisé - {etat.message}
            </p>
          )}
          {etat.type === 'echec' && <MessageEchec raison={etat.raison} message={etat.message} />}
        </div>
      </div>
    </section>
  );
}

/**
 * Chaque raison appelle une action différente. Un message unique obligerait
 * l'utilisateur à deviner s'il doit corriger son jeton, attendre du réseau, ou
 * prévenir l'administrateur du serveur.
 */
function MessageEchec({ raison, message }) {
  const textes = {
    [RAISONS.NON_CONFIGURE]: "Renseignez l’URL et le jeton avant de synchroniser.",
    [RAISONS.HORS_LIGNE]: "Serveur injoignable. Vos données restent enregistrées sur cet appareil ; la synchro se retentera plus tard.",
    [RAISONS.AUTH]: "Jeton d’appareil refusé. Vérifiez-le dans les réglages - l’usage local n’est pas bloqué.",
    [RAISONS.SERVEUR]: `${message}. L’application reste utilisable hors synchronisation.`,
  };
  // Hors-ligne est un état normal, pas une anomalie : il ne prend pas la
  // couleur d'alerte.
  const grave = raison === RAISONS.AUTH || raison === RAISONS.SERVEUR;
  return (
    <p style={{ margin: 0, fontSize: '0.85rem', color: grave ? 'var(--couleur-alerte)' : 'var(--couleur-encre-douce)' }}>
      {textes[raison] ?? message}
    </p>
  );
}
